// ─── Gardes — gestion parentale (hors vacances + durant les vacances) ──
// Deux parties :
//   • Hors vacances : TIMELINE de périodes de rythme (semaine alternée, 2-2-3,
//     modèles personnalisés…). Ajout/édition/suppression ; jour de départ libre.
//   • Durant les vacances : répartition de chaque période en 2 moitiés P1/P2.
// Écrit la garde par jour (partagée MoPCal).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT, Feuille, LigneSwipe, confirmerSuppression, frJM, frJMAcourt, HIT } from '@mop/core';
import {
  lireRythmes, sauverRythmes, sauverModelesPerso, construireConfig,
  insererRythme, supprimerRythme, editerRythme, labelModeleGarde, frCourt,
} from './services/rythmesGarde';
import { gardePourDate } from './services/modelesGarde';
import {
  chargerVacancesConfig, sauvegarderVacancesConfig, moitiesGardePeriode, emojiVacances, calculerMoities, decaleJour,
  lireGardeVacances, schemaVacancesPour, insererGardeVac, editerGardeVac, supprimerGardeVac, gardeVacancesJour, frDateV,
} from './services/vacances';
import { setGardeJour, effacerGardeJours, GARDE_COL_DEFAUT } from './services/garde';
import FeuilleRythme from './FeuilleRythme';
import FeuilleGardeVacances from './FeuilleGardeVacances';

const P = [{ id: 'parent1', l: 'P1' }, { id: 'parent2', l: 'P2' }];
const GARDE_COL = GARDE_COL_DEFAUT;   // source unique : services/garde
const pad = (n) => String(n).padStart(2, '0');
const fmtFR = frJM;        // JJ/MM (Core)
const fmtFRan = frJMAcourt; // JJ/MM/AA (Core)

// Garde attendue d'un jour d'après la répartition d'une période, selon le SCHÉMA
// GLOBAL (½1 / ½2 + alternance année impaire).
function gardeAttendue(p, dk, schema) {
  const { fin1 } = calculerMoities(p.debut, p.fin);
  const { moitie1Garde, moitie2Garde } = moitiesGardePeriode(p, schema);
  return dk <= fin1 ? moitie1Garde : moitie2Garde;
}
// Jours d'une période où la garde stockée diffère de la répartition (exceptions).
function exceptionsPeriode(p, garde, schema) {
  if (p.appliqueGarde === false) return [];   // garde non appliquée sur cette vacances
  const out = [];
  let c = new Date(p.debut + 'T00:00:00'); const fin = new Date(p.fin + 'T00:00:00');
  while (c <= fin) {
    const dk = `${c.getFullYear()}-${pad(c.getMonth() + 1)}-${pad(c.getDate())}`;
    const reel = garde?.[dk];
    if (reel && reel !== gardeAttendue(p, dk, schema)) out.push({ dk, garde: reel });
    c.setDate(c.getDate() + 1);
  }
  return out;
}

// Exceptions d'une période de RYTHME : jours dont la garde STOCKÉE (surcharge)
// diffère du rythme calculé. On n'itère que sur les surcharges (`garde` = ensemble
// fini), et on exclut les jours de vacances (qui ont leur propre répartition).
function exceptionsRythme(p, garde, perso, estVac) {
  const config = construireConfig(p.modeleId, p.debut, perso);
  if (!config) return [];
  const fin = p.fin || '9999-12-31';
  const out = [];
  for (const dk of Object.keys(garde || {})) {
    if (dk < p.debut || dk > fin) continue;
    if (estVac && estVac(dk)) continue;
    const attendu = gardePourDate(config, dk);
    if (garde[dk] && garde[dk] !== attendu) out.push({ dk, garde: garde[dk], attendu });
  }
  return out.sort((a, b) => (a.dk < b.dk ? -1 : 1));
}

const MOIS_COURTS = ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];

// Bloc d'exceptions RÉUTILISABLE (rythme ou vacances) : entête pliable, résumé
// des 2 parents + nb de jours échangés en leur faveur, filtre année/mois, liste
// au swipe et « Tout rétablir » sur le sous-ensemble filtré.
function BlocExceptions({ excs, s, UI, nomG, gardeCol, onEditerJour, onSupprimerJour, onResetAll, resetLabel }) {
  const t = useT();
  const [ouvert, setOuvert] = useState(false);
  const [fAn, setFAn] = useState(null);   // année filtrée ('2025') ou null
  const [fMo, setFMo] = useState(null);   // mois filtré (0-11) ou null

  const annees = [...new Set(excs.map(e => e.dk.slice(0, 4)))].sort();
  const parAnnee = fAn ? excs.filter(e => e.dk.slice(0, 4) === fAn) : excs;
  const moisPresents = [...new Set(parAnnee.map(e => Number(e.dk.slice(5, 7)) - 1))].sort((a, b) => a - b);
  const filtres = parAnnee.filter(e => fMo == null || Number(e.dk.slice(5, 7)) - 1 === fMo);
  const nbDe = (pid) => filtres.filter(e => e.garde === pid).length;

  return (
    <View style={s.excBox}>
      <TouchableOpacity style={s.excTeteRow} onPress={() => setOuvert(o => !o)} activeOpacity={0.7}>
        <Text style={s.excTitre}>⚠️ {t('garde.excTitre', { n: excs.length })}</Text>
        <Text style={s.excChev}>{ouvert ? '▾' : '▸'}</Text>
      </TouchableOpacity>
      {ouvert && (
        <>
          {/* Résumé : les 2 parents + nb de jours échangés en leur faveur (filtré). */}
          <View style={s.excResume}>
            {['parent1', 'parent2'].map(pid => (
              <View key={pid} style={s.excResChip}>
                <View style={[s.excDot, { backgroundColor: gardeCol[pid] || UI.muted }]} />
                <Text style={s.excResTxt} numberOfLines={1}>{nomG(pid)}</Text>
                <Text style={[s.excResNb, { color: gardeCol[pid] || UI.muted }]}>{nbDe(pid)} j</Text>
              </View>
            ))}
          </View>

          {/* Filtres : année (si plusieurs) puis mois présents. */}
          {annees.length > 1 && (
            <View style={s.fRow}>
              <TouchableOpacity style={[s.fChip, !fAn && s.fChipOn]} onPress={() => { setFAn(null); setFMo(null); }}>
                <Text style={[s.fChipTxt, !fAn && s.fChipTxtOn]}>{t('finances.toutes')}</Text>
              </TouchableOpacity>
              {annees.map(a => (
                <TouchableOpacity key={a} style={[s.fChip, fAn === a && s.fChipOn]} onPress={() => { setFAn(a); setFMo(null); }}>
                  <Text style={[s.fChipTxt, fAn === a && s.fChipTxtOn]}>{a}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
          {moisPresents.length > 1 && (
            <View style={s.fRow}>
              <TouchableOpacity style={[s.fChip, fMo == null && s.fChipOn]} onPress={() => setFMo(null)}>
                <Text style={[s.fChipTxt, fMo == null && s.fChipTxtOn]}>{t('garde.filtreTousMois')}</Text>
              </TouchableOpacity>
              {moisPresents.map(m => (
                <TouchableOpacity key={m} style={[s.fChip, fMo === m && s.fChipOn]} onPress={() => setFMo(m)}>
                  <Text style={[s.fChipTxt, fMo === m && s.fChipTxtOn]}>{MOIS_COURTS[m]}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {filtres.map(e => (
            <LigneSwipe key={e.dk} espacement={5} onEditer={() => onEditerJour(e)} onSupprimer={() => onSupprimerJour(e)}>
              <View style={s.excLigneRow}>
                <View style={[s.excDot, { backgroundColor: gardeCol[e.garde] || UI.muted }]} />
                <Text style={s.excDate}>{fmtFRan(e.dk)}</Text>
                <Text style={[s.excQui, { color: gardeCol[e.garde] || UI.muted }]} numberOfLines={1}>→ {nomG(e.garde)}</Text>
              </View>
            </LigneSwipe>
          ))}
          {filtres.length === 0
            ? <Text style={s.hintSwipe}>{t('garde.aucuneExcFiltre')}</Text>
            : <>
                <Text style={s.hintSwipe}>{t('garde.hintExcSwipe')}</Text>
                <TouchableOpacity style={s.resetAll} onPress={() => onResetAll(filtres.map(e => e.dk))}>
                  <Text style={s.resetAllTxt}>↩︎ {resetLabel} ({filtres.length})</Text>
                </TouchableOpacity>
              </>}
        </>
      )}
    </View>
  );
}

export default function FeuilleGardes({ visible, calId, garde = {}, parents, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const nomG = (g) => (g === 'parent1' ? (parents?.parent1?.nom || t('garde.parent1')) : (parents?.parent2?.nom || t('garde.parent2')));
  // Couleurs choisies par l'utilisateur (Configuration) ; repli sur les défauts.
  const gardeCol = { parent1: parents?.couleur1 || GARDE_COL.parent1, parent2: parents?.couleur2 || GARDE_COL.parent2 };
  const auj = new Date();
  const dkAuj = `${auj.getFullYear()}-${pad(auj.getMonth() + 1)}-${pad(auj.getDate())}`;

  // ── Rythme hors vacances (timeline) ──
  const [rythmes, setRythmes] = useState([]);
  const [perso, setPerso] = useState([]);
  const [formR, setFormR] = useState(null);        // { mode:'add'|'edit', index?, modeleId, debut } → ouvre FeuilleRythme

  // ── Vacances (répartition) ──
  const [periodes, setPeriodes] = useState([]);   // périodes de vacances (API : Toussaint, Noël…)
  const [cfgVac, setCfgVac] = useState({});        // config vacances brute (préserve zone/année/fériés)
  const [gardeVac, setGardeVac] = useState([]);    // TIMELINE de schémas-périodes de garde vacances
  const [formGV, setFormGV] = useState(null);      // { mode:'add'|'edit', index?, init } → FeuilleGardeVacances
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // Config complète (pour le calcul de la garde des vacances à l'affichage).
  const cfgComplet = { ...cfgVac, periodes, gardeVacances: gardeVac };
  // Un jour est-il gouverné par la garde des vacances ? → exclu des exceptions de rythme.
  const estVac = (dk) => gardeVacancesJour(dk, cfgComplet) != null;

  useEffect(() => {
    if (!visible || !calId) return;
    lireRythmes(calId).then(({ rythmes: r, perso: mp }) => { setRythmes(r); setPerso(mp); }).catch(() => {});
    chargerVacancesConfig(calId).then(cfg => {
      setCfgVac(cfg || {});
      if (Array.isArray(cfg?.periodes)) setPeriodes(cfg.periodes);
      setGardeVac(lireGardeVacances(cfg || {}));
    }).catch(() => {});
  }, [visible, calId]);

  const nomP = (id) => (id === 'parent1' ? (parents?.parent1?.nom || t('garde.parent1')) : (parents?.parent2?.nom || t('garde.parent2')));

  const ouvrirAjout = () => setFormR({ mode: 'add', modeleId: 'alternee', debut: dkAuj });
  const ouvrirEdition = (i) => { const p = rythmes[i]; setFormR({ mode: 'edit', index: i, modeleId: p.modeleId, debut: p.debut, fin: p.fin || '', estDernier: i === rythmes.length - 1 }); };

  // Validation depuis la modale FeuilleRythme : renvoie { ok, err } et persiste.
  function validerForm({ modeleId, debut, fin }) {
    if (!formR) return { ok: false, err: t('commun.formulaireFerme') };
    const r = formR.mode === 'add'
      ? insererRythme(rythmes, modeleId, debut)
      : editerRythme(rythmes, formR.index, { modeleId, debut, fin });
    if (!r.ok) return { ok: false, err: r.err, errP: r.errP };
    setRythmes(r.rythmes); sauverRythmes(calId, r.rythmes).catch(() => {});
    return { ok: true };
  }
  function supprimer(i) {
    confirmerSuppression(t('vacances.supprimerTitre'), t('garde.supprimerPeriodeMsg'), () => {
      const l = supprimerRythme(rythmes, i); setRythmes(l); sauverRythmes(calId, l).catch(() => {});
    });
  }
  // Bascule « appliquer ou pas » sur un rythme. La garde du rythme est CALCULÉE à
  // l'affichage (aucune écriture en base) ; ce drapeau décide si elle apparaît.
  function toggleApplique(i) {
    const l = rythmes.map((r, k) => (k === i ? { ...r, applique: r.applique === false } : r));
    setRythmes(l); sauverRythmes(calId, l).catch(() => {});
  }
  function enregistrerPerso(modele) {
    const l = [...perso.filter(m => m.id !== modele.id), modele];
    setPerso(l); sauverModelesPerso(calId, l).catch(() => {});
  }

  // Persiste config vacances (timeline gardeVacances + périodes) SANS matérialiser
  // la garde : la répartition est calculée à l'affichage (cf. gardeVacancesJour).
  const persisterVac = (nextPeriodes, nextGardeVac) => {
    const per = nextPeriodes ?? periodes;
    const gv = nextGardeVac ?? gardeVac;
    if (nextPeriodes) setPeriodes(nextPeriodes);
    if (nextGardeVac) setGardeVac(nextGardeVac);
    sauvegarderVacancesConfig(calId, { ...cfgVac, gardeVacances: gv, periodes: per }).catch(() => {});
  };
  // Timeline « garde vacances » (schémas-périodes) — comme les rythmes.
  const ouvrirAjoutGV = () => setFormGV({ mode: 'add', init: { debut: dkAuj, premiereMoitieGarde: 'parent1', alternerAnneeImpaire: false, debutVeille: true } });
  const ouvrirEditionGV = (i) => setFormGV({ mode: 'edit', index: i, init: gardeVac[i] });
  function validerGV(schema) {
    if (!formGV) return { ok: false, err: t('commun.formulaireFerme') };
    const r = formGV.mode === 'add' ? insererGardeVac(gardeVac, schema) : editerGardeVac(gardeVac, formGV.index, schema);
    if (!r.ok) return { ok: false, err: r.err, errP: r.errP };
    persisterVac(null, r.liste);
    return { ok: true };
  }
  const supprimerGV = (i) => confirmerSuppression(t('garde.supprimerGardeVacancesTitre'), t('garde.supprimerGardeVacancesMsg'), () => persisterVac(null, supprimerGardeVac(gardeVac, i)));
  const toggleAppliqueGV = (i) => persisterVac(null, gardeVac.map((g, k) => (k === i ? { ...g, applique: g.applique === false } : g)));
  // Appliquer (ou non) la garde des vacances sur UNE vacances (Toussaint, Noël…).
  const toggleAppliqueVac = (i) => persisterVac(periodes.map((p, k) => (k === i ? { ...p, appliqueGarde: p.appliqueGarde === false } : p)));

  const actives = periodes.filter(p => p.actif !== false);

  return (
   <>
    <Feuille visible={visible} titre={t('garde.titre')} emoji="🧷" onFermer={onFermer}>
      {/* ── Hors vacances : timeline de rythmes ── */}
      <View style={s.bloc}>
        <View style={s.titreRow}>
          <Text style={s.titre}>{t('garde.horsVacancesRythme')}</Text>
          <View style={s.actions}>
            <TouchableOpacity onPress={ouvrirAjout} hitSlop={HIT} accessibilityLabel={t('garde.ajouterPeriode')}>
              <Text style={s.applyIc}>＋</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Liste des périodes appliquées */}
        {rythmes.length === 0 ? (
          <Text style={s.aide}>{t('garde.aucunRythme')}</Text>
        ) : rythmes.map((p, i) => {
          const enCours = !p.fin;
          const excs = exceptionsRythme(p, garde, perso, estVac);
          return (
            <View key={`${p.debut}-${p.modeleId}-${i}`} style={{ marginBottom: 6 }}>
              <LigneSwipe onEditer={() => ouvrirEdition(i)} onSupprimer={() => supprimer(i)} espacement={0}>
                <TouchableOpacity style={s.rythmeCarte} activeOpacity={0.7} onPress={() => ouvrirEdition(i)}>
                  <View style={s.rythmeTete}>
                    <Text style={s.rythmeNom} numberOfLines={1}>{labelModeleGarde(p.modeleId, perso, t)}</Text>
                    {enCours && <Text style={s.badge}>{t('garde.enCours')}</Text>}
                  </View>
                  <View style={s.rythmeBas}>
                    <Text style={s.rythmePeriode}>{p.fin ? t('garde.duAu', { debut: frCourt(p.debut), fin: frCourt(p.fin) }) : t('garde.duSansFin', { debut: frCourt(p.debut) })}</Text>
                    <TouchableOpacity onPress={() => toggleApplique(i)} hitSlop={HIT} style={[s.applChip, p.applique === false && s.applChipOff]} accessibilityLabel={t('garde.appliquerRythmeAcc')}>
                      <Text style={[s.applChipTxt, p.applique === false && s.applChipTxtOff]}>{p.applique === false ? t('garde.nePasAppliquer') : t('garde.appliquer')}</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              </LigneSwipe>
              {excs.length > 0 && (
                <BlocExceptions excs={excs} s={s} UI={UI} nomG={nomG} gardeCol={gardeCol}
                  onEditerJour={(e) => setGardeJour(calId, e.dk, e.garde === 'parent1' ? 'parent2' : 'parent1').catch(() => {})}
                  onSupprimerJour={(e) => setGardeJour(calId, e.dk, null).catch(() => {})}
                  onResetAll={(dks) => confirmerSuppression(t('garde.retablirRythme'), t('garde.retablirRythmeMsg', { n: dks.length }), () => effacerGardeJours(calId, dks).catch(() => {}))}
                  resetLabel={t('garde.retablirRythme')} />
              )}
            </View>
          );
        })}
        {rythmes.length > 0 && <Text style={s.hintSwipe}>{t('garde.hintRythme')}</Text>}

        <Text style={s.aide}>{t('garde.semaineAlterneeAide', { nom: nomP('parent1') })}</Text>
      </View>

      {/* ── Durant les vacances : TIMELINE de gardes vacances (schémas-périodes) ── */}
      <View style={s.bloc}>
        <View style={s.titreRow}>
          <Text style={s.titre}>{t('garde.durantVacancesRepartition')}</Text>
          <View style={s.actions}>
            <TouchableOpacity onPress={ouvrirAjoutGV} hitSlop={HIT} accessibilityLabel={t('garde.ajouterGardeVacances')}>
              <Text style={s.applyIc}>＋</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Chaque « garde vacances » = une période (début → fin ou sans fin) + un
            schéma de répartition (qui a la 1re moitié les années paires/impaires,
            veille au soir). Appliquée ou non ; on peut en enchaîner plusieurs. */}
        {gardeVac.length === 0 ? (
          <Text style={s.aide}>{t('garde.aucuneGardeVacances')}</Text>
        ) : gardeVac.map((g, i) => {
          const enCours = !g.fin;
          const applique = g.applique !== false;
          const p1 = g.premiereMoitieGarde || 'parent1';
          const p1Impair = g.alternerAnneeImpaire ? (p1 === 'parent1' ? 'parent2' : 'parent1') : p1;
          return (
            <View key={`gv-${g.debut}-${i}`} style={{ marginBottom: 6 }}>
              <LigneSwipe onEditer={() => ouvrirEditionGV(i)} onSupprimer={() => supprimerGV(i)} espacement={0}>
                <TouchableOpacity style={[s.rythmeCarte, !applique && s.periodeOff]} activeOpacity={0.7} onPress={() => ouvrirEditionGV(i)}>
                  <View style={s.rythmeTete}>
                    <Text style={s.rythmeNom} numberOfLines={1}>🏖️ {t('garde.gardeVacancesLabel')}</Text>
                    {enCours && <Text style={s.badge}>{t('garde.enCours')}</Text>}
                  </View>
                  <Text style={[s.rythmePeriode, { marginTop: 5 }]}>{g.fin ? t('garde.duAu', { debut: frDateV(g.debut), fin: frDateV(g.fin) }) : t('garde.duSansFin', { debut: frDateV(g.debut) })}</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
                    <View style={s.excResChip}>
                      <View style={[s.excDot, { backgroundColor: gardeCol[p1] || UI.muted }]} />
                      <Text style={s.gvResTxt}>{t('garde.demiUnAnPair', { nom: nomG(p1) })}</Text>
                    </View>
                    <View style={s.excResChip}>
                      <View style={[s.excDot, { backgroundColor: gardeCol[p1Impair] || UI.muted }]} />
                      <Text style={s.gvResTxt}>{t('garde.demiUnAnImpair', { nom: nomG(p1Impair) })}</Text>
                    </View>
                  </View>
                  <View style={s.rythmeBas}>
                    <Text style={s.rythmePeriode}>{g.debutVeille !== false ? t('garde.debuteVeille') : t('garde.debutePremierJour')}</Text>
                    <TouchableOpacity onPress={() => toggleAppliqueGV(i)} hitSlop={HIT} style={[s.applChip, !applique && s.applChipOff]} accessibilityLabel={t('garde.appliquerGardeVacancesAcc')}>
                      <Text style={[s.applChipTxt, !applique && s.applChipTxtOff]}>{applique ? t('garde.appliquer') : t('garde.nePasAppliquer')}</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              </LigneSwipe>
            </View>
          );
        })}
        {gardeVac.length > 0 && <Text style={s.hintSwipe}>{t('garde.hintGardeVacances')}</Text>}

        {/* Une carte PAR vacances : moitiés calculées (lecture) selon le schéma-période
            actif ce jour-là + appliquer ou non la garde sur cette vacances. */}
        <Text style={[s.lbl, { marginTop: 6 }]}>{t('garde.repartitionParVacances')}</Text>
        {actives.length === 0 ? (
          <Text style={[s.aide, { marginTop: 6 }]}>{t('garde.aucunePeriodeVacances')}</Text>
        ) : periodes.map((p, i) => (p.actif === false ? null : (() => {
          const schema = schemaVacancesPour(cfgComplet, p.debut);
          if (!schema) return (
            <View key={i} style={s.periode}>
              <View style={s.periodeTete}>
                <Text style={{ fontSize: 16 }}>{emojiVacances(p.nom)}</Text>
                <Text style={s.periodeNom} numberOfLines={1}>{p.nom}</Text>
              </View>
              <Text style={[s.aide, { marginTop: 4 }]}>{t('garde.aucuneGardeVacancesCouvrant', { date: frDateV(p.debut) })}</Text>
            </View>
          );
          const m = calculerMoities(p.debut, p.fin);
          const { moitie1Garde, moitie2Garde } = moitiesGardePeriode(p, schema);
          const applique = p.appliqueGarde !== false;
          const veille = schema.debutVeille !== false;
          const deb1 = veille ? decaleJour(m.debut1, -1) : m.debut1;   // 1re moitié dès la veille au soir
          const nb1 = veille ? m.nbJours1 + 1 : m.nbJours1;
          const rows = [
            { lab: '½1', g: moitie1Garde, bornes: `${fmtFRan(deb1)}→${fmtFRan(m.fin1)} · ${nb1}j` },
            { lab: '½2', g: moitie2Garde, bornes: `${fmtFRan(m.debut2)}→${fmtFRan(m.fin2)} · ${m.nbJours2}j` },
          ];
          const excs = exceptionsPeriode(p, garde, schema);
          return (
          <View key={i} style={[s.periode, !applique && s.periodeOff]}>
            <View style={s.periodeTete}>
              <Text style={{ fontSize: 16 }}>{emojiVacances(p.nom)}</Text>
              <Text style={s.periodeNom} numberOfLines={1}>{p.nom}</Text>
              <TouchableOpacity onPress={() => toggleAppliqueVac(i)} hitSlop={HIT} style={[s.applChip, !applique && s.applChipOff]} accessibilityLabel={t('garde.appliquerGardeSurVacancesAcc')}>
                <Text style={[s.applChipTxt, !applique && s.applChipTxtOff]}>{applique ? t('garde.appliquer') : t('garde.nePasAppliquer')}</Text>
              </TouchableOpacity>
            </View>
            {applique && (
              <View style={{ gap: 8, marginTop: 8 }}>
                {rows.map(r => {
                  const c = gardeCol[r.g] || UI.muted;
                  return (
                    <View key={r.lab} style={s.moitieRow}>
                      <Text style={s.moitieLab}>{r.lab}</Text>
                      <Text style={s.moitieBornes} numberOfLines={1}>{r.bornes}</Text>
                      <View style={[s.moitieBtn, { backgroundColor: c + '22', borderColor: c }]}>
                        <View style={[s.excDot, { backgroundColor: c }]} />
                        <Text style={[s.moitieBtnTxt, { color: c }]} numberOfLines={1}>{nomG(r.g)}</Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            )}
            {applique && excs.length > 0 && (
              <BlocExceptions excs={excs} s={s} UI={UI} nomG={nomG} gardeCol={gardeCol}
                onEditerJour={(e) => setGardeJour(calId, e.dk, e.garde === 'parent1' ? 'parent2' : 'parent1').catch(() => {})}
                onSupprimerJour={(e) => setGardeJour(calId, e.dk, null).catch(() => {})}
                onResetAll={(dks) => confirmerSuppression(t('garde.retablirTitre'), t('garde.retablirRepartitionMsg', { n: dks.length }), () => effacerGardeJours(calId, dks).catch(() => {}))}
                resetLabel={t('garde.retablirRepartition')} />
            )}
          </View>
          );
        })()))}
        <Text style={[s.aide, { marginTop: 10 }]}>{t('garde.repartitionCalculeeAide')}</Text>
      </View>

      {!!msg && <Text style={s.msg}>{msg}</Text>}
      <Text style={s.note}>{t('garde.noteParents', { p1: nomP('parent1'), p2: nomP('parent2') })}</Text>
    </Feuille>

    {/* Modale d'ajout / édition de période — rendue en SŒUR (calque). */}
    <FeuilleRythme visible={!!formR} mode={formR?.mode} initModeleId={formR?.modeleId} initDebut={formR?.debut}
      initFin={formR?.fin} estDernier={formR?.estDernier !== false}
      perso={perso} rythmes={rythmes} parents={parents}
      onValider={validerForm} onEnregistrerPerso={enregistrerPerso} onFermer={() => setFormR(null)} />

    {/* Modale d'ajout / édition d'une garde vacances (schéma-période) — SŒUR (calque). */}
    <FeuilleGardeVacances visible={!!formGV} mode={formGV?.mode} init={formGV?.init} parents={parents}
      onValider={validerGV} onFermer={() => setFormGV(null)} />
   </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  bloc:  { borderWidth: 1, borderColor: UI.border, borderRadius: 14, backgroundColor: UI.card, padding: 13, marginBottom: 14 },
  titreRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  titre: { fontSize: 14.5, fontWeight: '800', color: UI.ink, flex: 1 },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  applyIc: { fontSize: 20, color: UI.accent, fontWeight: '600' },
  // Timeline
  rythmeCarte: { backgroundColor: UI.champ, borderRadius: 12, borderWidth: 0.5, borderColor: UI.champBord, padding: 11, marginBottom: 0 },
  rythmeTete: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rythmeNom: { fontSize: 14, fontWeight: '800', color: UI.ink, flex: 1 },
  badge: { fontSize: 10.5, fontWeight: '800', color: UI.accent, backgroundColor: UI.accentTint, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, overflow: 'hidden' },
  rythmePeriode: { fontSize: 12, color: UI.muted, fontWeight: '600' },
  rythmeBas: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 5 },
  applChip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, backgroundColor: UI.accentTint, borderWidth: 1, borderColor: UI.accent },
  applChipOff: { backgroundColor: UI.champ, borderColor: UI.champBord },
  applChipTxt: { fontSize: 11.5, fontWeight: '800', color: UI.accent },
  applChipTxtOff: { color: UI.muted },
  hintSwipe: { fontSize: 11, color: UI.faint, marginTop: 4, marginBottom: 2, lineHeight: 15 },
  // Formulaire
  formR: { marginTop: 10, borderTopWidth: 0.5, borderTopColor: UI.border, paddingTop: 10 },
  formTitre: { fontSize: 13, fontWeight: '800', color: UI.ink, marginBottom: 6 },
  opt:   { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 8, borderRadius: 10, marginBottom: 4 },
  optOn: { backgroundColor: UI.accentTint },
  radio: { fontSize: 17, color: UI.accent, width: 20, textAlign: 'center' },
  optNom:{ fontSize: 14, fontWeight: '700', color: UI.ink },
  optDesc:{ fontSize: 11.5, color: UI.muted, marginTop: 1 },
  optEdit: { fontSize: 15, paddingHorizontal: 4 },
  persoBtn: { marginTop: 4, marginBottom: 2, paddingVertical: 9, alignItems: 'center', borderRadius: 10, borderWidth: 1, borderColor: UI.accent, borderStyle: 'dashed' },
  persoBtnTxt: { fontSize: 12.5, fontWeight: '700', color: UI.accent },
  lbl:   { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 10, marginBottom: 7 },
  champBtn: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, borderWidth: 0.5, borderColor: UI.champBord },
  champVal: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  err: { fontSize: 12, color: '#C62828', marginTop: 9 },
  apercu: { fontSize: 12, color: UI.muted, fontWeight: '600', marginTop: 8 },
  formBtns: { flexDirection: 'row', gap: 10, marginTop: 12 },
  btnAnnuler: { flex: 1, paddingVertical: 11, borderRadius: 11, borderWidth: 1, borderColor: UI.border, alignItems: 'center' },
  btnAnnulerTxt: { fontSize: 13.5, fontWeight: '700', color: UI.muted },
  btnValider: { flex: 1, paddingVertical: 11, borderRadius: 11, backgroundColor: UI.accent, alignItems: 'center' },
  btnValiderTxt: { fontSize: 13.5, fontWeight: '800', color: '#fff' },
  aide:  { fontSize: 11.5, color: UI.faint, marginTop: 8, lineHeight: 16 },
  // Vacances
  periode: { borderWidth: 1, borderColor: UI.border, borderRadius: 11, padding: 10, marginTop: 8 },
  periodeOff: { opacity: 0.55 },
  periodeTete: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  periodeNom: { flex: 1, fontSize: 13.5, fontWeight: '700', color: UI.ink },
  moitieTete: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  moitieRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  moitieLab: { fontSize: 12.5, color: UI.ink, fontWeight: '800', width: 24 },
  moitieBornes: { flex: 1, fontSize: 11, color: UI.faint, fontWeight: '600' },
  moitieBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, borderWidth: 1 },
  moitieBtnTxt: { fontSize: 12, fontWeight: '800' },
  mini:  { paddingHorizontal: 10, paddingVertical: 8, borderRadius: 8, backgroundColor: UI.champ, borderWidth: 1, borderColor: UI.champBord, alignItems: 'center' },
  miniTxt:{ fontSize: 12, color: UI.ink, fontWeight: '700' },
  // Bascule « inverser les années impaires »
  alternRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12 },
  alternTxt: { fontSize: 13, fontWeight: '700', color: UI.ink },
  alternSub: { fontSize: 11, color: UI.faint, marginTop: 1 },
  // Reset en lot des surcharges d'une période
  resetAll: { marginTop: 8, paddingVertical: 8, alignItems: 'center', borderRadius: 9, borderWidth: 1, borderColor: UI.border, borderStyle: 'dashed' },
  resetAllTxt: { fontSize: 12, fontWeight: '700', color: UI.muted },
  // Résumé des 2 parents + compteurs de jours échangés
  excResume: { flexDirection: 'row', flexWrap: 'wrap', gap: 14, marginTop: 8, marginBottom: 2 },
  excResChip: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  excResTxt: { fontSize: 12.5, fontWeight: '700', color: UI.ink },
  excResNb: { fontSize: 12.5, fontWeight: '800' },
  gvResTxt: { fontSize: 11.5, fontWeight: '700', color: UI.muted },
  // Filtres année / mois
  fRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  fChip: { paddingHorizontal: 9, paddingVertical: 4, borderRadius: 999, borderWidth: 1, borderColor: UI.champBord, backgroundColor: UI.champ },
  fChipOn: { backgroundColor: UI.accentTint, borderColor: UI.accent },
  fChipTxt: { fontSize: 11, fontWeight: '700', color: UI.muted },
  fChipTxtOn: { color: UI.accent },
  excBox: { marginTop: 9, paddingTop: 8, borderTopWidth: 0.5, borderTopColor: UI.border },
  excTeteRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 2 },
  excTitre: { fontSize: 11.5, fontWeight: '800', color: '#C77700', flex: 1 },
  excChev: { fontSize: 13, color: UI.muted, paddingHorizontal: 4 },
  excLigneRow: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.champ, borderRadius: 9, borderWidth: 0.5, borderColor: UI.champBord, paddingHorizontal: 10, paddingVertical: 8 },
  excDot: { width: 9, height: 9, borderRadius: 5 },
  excDate: { fontSize: 12.5, fontWeight: '700', color: UI.ink, minWidth: 44 },
  excQui: { flex: 1, fontSize: 12.5, fontWeight: '700' },
  excLigne: { fontSize: 11.5, fontWeight: '600', marginLeft: 4 },
  excPlus: { fontSize: 11, color: UI.faint, marginLeft: 4, fontStyle: 'italic' },
  msg:   { fontSize: 12.5, color: UI.accent, textAlign: 'center', marginTop: 4 },
  note:  { fontSize: 11.5, color: UI.faint, marginTop: 10, textAlign: 'center', lineHeight: 16 },
});
